/**
 * ロジック図の線の引き方 — 業務フロー図の作図規約（`docs/web/spec-console-flow-drawing.md`）の規則 2〜7 を
 * そのまま当てる（Dhik 2026-10-06「業務フロー側のルールをそのまま持ってこればいいだけ」）。
 *
 * 線の形と位置の詰め方は業務フロー図のエンジン（`business-flow-diagram/edges.ts`）の関数を呼ぶ。
 * ここが持つのは、ロジック図の寸法（`logic-layout.ts` が取ったガターと水路の帯）を当てはめることだけで、
 * 経路の決め方は持たない。2 つの図で線の引き方が違うと、読み手が図ごとに読み方を変えることになる。
 *
 * - **形は 2 つ**（規則 2）: 同じ高さで隣の列へ行く線だけが直線。それ以外は、始点の列のガターで縦に
 *   降り、始点の段の水路を横に走り、終点の手前のガターで縦に上がって入る。水路は段の下にあるので、
 *   線が箱の高さを横に走って箱を貫くことが無い
 * - **位置は区間で詰める**（規則 3）: 水路は段ごと、縦線は出る列ごと、上がる位置は入る列ごとに、
 *   区間が重なる線だけが別の位置を取る（`packLanes`）
 * - **根元をずらす**（規則 4）: 同じ箱から出る線は根元の高さを散らす（`departureY`）
 * - **縁から出て縁へ入る**（規則 5）: ひし形から出る線は、ずらした高さでのひし形の縁から出す
 * - **ラベルは帯に置き、当たったら下へ逃がす**（規則 6、`resolveEdgeLabelPositions`）
 * - **分岐の枝は色で対応を付ける**（規則 7、`branchColorOf`）
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の線を、業務フロー図の作図規約（直線か水路経由の 2 形・区間で詰める位置・根元のずらし・ラベルの逃がし・枝の色）で組み立てる。
 * @graph-connects none
 */
import {
  buildOrthogonalPath,
  departureY,
  laneStep,
  packLanes,
  resolveEdgeLabelPositions,
} from '../business-flow-diagram/edges.js';
import { branchColorOf } from '../business-flow-diagram/nodes.js';
import {
  LOGIC_CHANNEL_STEP,
  LOGIC_GUTTER_INSET,
  LOGIC_GUTTER_LINE_ZONE,
  LOGIC_GUTTER_RISE_ZONE,
  LOGIC_GUTTER_WITH_LABEL,
  LOGIC_LABEL_ZONE_WIDTH,
  type LogicBox,
  type LogicLayout,
} from './logic-layout.js';
import { atOr, getOr, type LogicEdge, type LogicNode } from './logic-model.js';

/** ガター内で線を 1 本ずつずらす量（業務フロー図の `GUTTER_STEP` と同じ考え方）。 @graph-connects none */
const LOGIC_GUTTER_STEP = 6;

/** ラベルを始点の根元より上へ外す量。 @graph-connects none */
const LABEL_LIFT = 14;

/**
 * 線 1 本の描き方（経路 ・ ラベルの位置 ・ 枝の色）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の線 1 本の経路・条件ラベルの位置・分岐の枝の色を表す。
 * @graph-connects none
 */
export interface LogicEdgeRoute {
  /** 材料の線の並びでの位置 */
  readonly index: number;
  /** SVG のパス */
  readonly path: string;
  /** 条件ラベル。無ければ null */
  readonly label: string | null;
  readonly labelX: number;
  readonly labelY: number;
  /** 分岐の枝の色。分岐から出ていない線、1 本しか出ていない線は null */
  readonly branchColor: string | null;
}

/** 箱の縦の中心。 @graph-connects none */
function centerY(box: LogicBox): number {
  return box.y + box.h / 2;
}

/**
 * 始点の箱から線が出る X。ひし形は縁が斜めなので、根元をずらした高さでの縁まで寄せる（規則 5）。
 * @graph-connects none
 */
function exitX(box: LogicBox, kind: LogicNode['kind'], y: number): number {
  const right = box.x + box.w;
  if (kind !== 'branch') return right;
  return right - (Math.abs(y - centerY(box)) / (box.h / 2)) * (box.w / 2);
}

/** 2 つの値が作る区間。 @graph-connects none */
function spanOf(a: number, b: number): { from: number; to: number } {
  return { from: Math.min(a, b), to: Math.max(a, b) };
}

/**
 * 同じ群の中だけで位置を詰め、線ごとの座標へ変換する（業務フロー図の `assignByGroup` と同じ手順）。
 * @graph-connects packLanes [calls] 区間の重なりを避けた位置番号の割り当て
 */
function assignByGroup<T>(
  items: readonly T[],
  groupOf: (item: T) => number,
  intervalOf: (item: T, index: number) => { from: number; to: number },
  coordinateOf: (item: T, lane: number, laneCount: number) => number,
): number[] {
  const groups = new Map<number, { item: T; index: number }[]>();
  items.forEach((item, index) => {
    const key = groupOf(item);
    groups.set(key, [...getOr(groups, key, []), { item, index }]);
  });
  const lanes = items.map(() => 0);
  const counts = items.map(() => 1);
  for (const members of groups.values()) {
    const assigned = packLanes(members.map(({ item, index }) => intervalOf(item, index)));
    const used = Math.max(...assigned) + 1;
    members.forEach(({ index }, position) => {
      lanes[index] = atOr(assigned, position, 0);
      counts[index] = used;
    });
  }
  return items.map((item, index) =>
    coordinateOf(item, atOr(lanes, index, 0), atOr(counts, index, 1)),
  );
}

/**
 * ロジック図の線を、業務フロー図の作図規約で引く。
 *
 * @param nodes 描く節点
 * @param edges 描く線
 * @param layout 置き場所（ガターと水路の帯を含む）
 * @returns 線ごとの経路 ・ ラベルの位置 ・ 枝の色（端点の箱が置き場所に無い線は含めない）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の線を、業務フロー図と同じ規則（直線か水路経由・区間で詰める位置・根元のずらし・ラベルの逃がし・枝の色）で経路に組み立てる。
 * @graph-connects buildOrthogonalPath [calls] 直交パスの組み立て
 * @graph-connects departureY [calls] 同じ箱から出る線の根元の高さ
 * @graph-connects assignByGroup [calls] 水路・縦線・上がる位置の割り当て
 * @graph-connects resolveEdgeLabelPositions [calls] ラベルの衝突解決
 * @graph-connects branchColorOf [calls] 分岐の枝の色
 */
export function routeLogicEdges(
  nodes: readonly LogicNode[],
  edges: readonly LogicEdge[],
  layout: LogicLayout,
): LogicEdgeRoute[] {
  const kindOf = new Map(nodes.map((node) => [node.key, node.kind]));
  const routes = edges.flatMap((edge, index) => {
    const source = layout.boxes.get(edge.from);
    const target = layout.boxes.get(edge.to);
    if (source === undefined || target === undefined) return [];
    const siblings = edges.filter((other) => other.from === edge.from);
    const order = siblings.indexOf(edge);
    const total = siblings.length;
    const sourceColumn = getOr(layout.columnOf, edge.from, 0);
    const targetColumn = getOr(layout.columnOf, edge.to, 0);
    const startY = departureY(centerY(source), order, total);
    const sourceKind = getOr(kindOf, edge.from, 'rule');
    return [
      {
        edge,
        index,
        order,
        total,
        sourceKind,
        startX: exitX(source, sourceKind, startY),
        startY,
        endX: target.x,
        endY: centerY(target),
        adjacent: targetColumn === sourceColumn + 1,
        sourceLane: getOr(layout.laneOf, edge.from, 0),
        sourceColumn,
        targetColumn,
        sourceCellRight:
          atOr(layout.columnLeft, sourceColumn, 0) + atOr(layout.columnWidths, sourceColumn, 0),
        targetCellLeft: atOr(layout.columnLeft, targetColumn, 0),
      },
    ];
  });

  const runY = assignByGroup(
    routes,
    (route) => route.sourceLane,
    /* 水路が実際に走る範囲は、始点のガターから終点のガターまで。両側へガター 1 本ぶん広く取る。 */
    (route) => ({
      from: Math.min(route.sourceCellRight, route.targetCellLeft) - LOGIC_GUTTER_WITH_LABEL,
      to: Math.max(route.sourceCellRight, route.targetCellLeft) + LOGIC_GUTTER_WITH_LABEL,
    }),
    (route, lane, count) => {
      const channel = atOr(layout.channels, route.sourceLane, { top: 0, bottom: 0 });
      return (
        channel.bottom - laneStep(channel.bottom - channel.top, count, LOGIC_CHANNEL_STEP) * lane
      );
    },
  );
  const turnX = assignByGroup(
    routes,
    (route) => route.sourceColumn,
    (route, index) => spanOf(route.startY, atOr(runY, index, route.startY)),
    (route, lane, count) =>
      route.sourceCellRight +
      LOGIC_GUTTER_INSET +
      laneStep(LOGIC_GUTTER_LINE_ZONE - LOGIC_GUTTER_INSET * 2, count, LOGIC_GUTTER_STEP) * lane,
  );
  const riseX = assignByGroup(
    routes,
    (route) => route.targetColumn,
    (route, index) => spanOf(atOr(runY, index, route.endY), route.endY),
    (route, lane, count) =>
      route.targetCellLeft -
      LOGIC_GUTTER_INSET -
      laneStep(LOGIC_GUTTER_RISE_ZONE - LOGIC_GUTTER_INSET * 2, count, LOGIC_GUTTER_STEP) * lane,
  );

  const drawn = routes.map((route, position) => ({
    route,
    path: buildOrthogonalPath({
      startX: route.startX,
      startY: route.startY,
      endX: route.endX,
      endY: route.endY,
      adjacent: route.adjacent,
      turnX: atOr(turnX, position, route.startX),
      runY: atOr(runY, position, route.startY),
      riseX: atOr(riseX, position, route.endX),
    }),
    seed: {
      x:
        route.sourceCellRight +
        LOGIC_GUTTER_LINE_ZONE +
        LOGIC_GUTTER_INSET +
        LOGIC_LABEL_ZONE_WIDTH / 2,
      y: route.startY - LABEL_LIFT,
    },
  }));

  const resolved = new Map(
    resolveEdgeLabelPositions(
      drawn.flatMap((entry) =>
        entry.route.edge.label === undefined
          ? []
          : [
              {
                id: String(entry.route.index),
                label: entry.route.edge.label,
                x: entry.seed.x,
                y: entry.seed.y,
              },
            ],
      ),
      drawn.map((entry) => entry.path),
      [...layout.boxes.values()].map((box) => ({
        left: box.x,
        right: box.x + box.w,
        top: box.y,
        bottom: box.y + box.h,
      })),
    ).map((label) => [Number(label.id), label]),
  );

  return drawn.map(({ route, path, seed }) => {
    const position = resolved.get(route.index) ?? seed;
    return {
      index: route.index,
      path,
      label: route.edge.label ?? null,
      labelX: position.x,
      labelY: position.y,
      branchColor: route.sourceKind === 'branch' ? branchColorOf(route.order, route.total) : null,
    };
  });
}
